import { useEffect, useRef, useState } from "react";
import { ConfirmDialog, Field, Textarea } from "../../ui";
import "./styles.scss";

type BanPlayerDialogProps = {
  playerName: string;
  isBanned: boolean;
  isOpen: boolean;
  onBan: (reason: string) => void;
  onUnban: () => void;
  onClose: (update?: boolean) => void;
};

/** Ban (with an optional reason) or unban a player: a wrapper round the kit's ConfirmDialog. */
const BanPlayerDialog = (props: BanPlayerDialogProps) => {
  const [reason, setReason] = useState("");

  // While the dialog plays its exit animation the parent may already have cleared the player; keep showing the one
  // it was opened for.
  const shown = useRef({ isBanned: props.isBanned, playerName: props.playerName });
  if (props.isOpen) shown.current = { isBanned: props.isBanned, playerName: props.playerName };
  const { isBanned, playerName } = shown.current;

  useEffect(() => {
    if (props.isOpen) setReason("");
  }, [props.isOpen]);

  const handleConfirm = () => {
    // Started, not awaited: the dialog closes straight away, as it always has
    if (props.isBanned) {
      props.onUnban();
    } else {
      props.onBan(reason);
    }
  };

  return (
    <ConfirmDialog
      open={props.isOpen}
      onClose={(confirmed) => props.onClose(confirmed ? true : undefined)}
      onConfirm={handleConfirm}
      title={isBanned ? "Unban Player" : "Ban Player"}
      message={
        isBanned
          ? `Are you sure you want to unban ${playerName}? They will regain access to all services.`
          : `Are you sure you want to ban ${playerName}? They will be unable to register for tournaments, reserve courts, or be added to teams.`
      }
      confirmLabel={isBanned ? "Unban" : "Ban"}
      tone={isBanned ? "primary" : "danger"}
    >
      {!isBanned && (
        <Field label="Reason (optional)" className="ban-player-dialog__reason">
          {(id) => (
            <Textarea
              id={id}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter a reason for the ban..."
              rows={3}
            />
          )}
        </Field>
      )}
    </ConfirmDialog>
  );
};

export default BanPlayerDialog;
